import { MIN_PRODUCED_FOR_MEASURED } from "@opus-dt/shared-types";
import { Banner } from "@/components/ui/notify/Banner";
import { QueryErrorState } from "@/components/ui/notify/QueryErrorState";
import type { SandboxMachine } from "@/types/sim-sandbox.types";

interface Props {
  machine: SandboxMachine;
  /** Erro do preview do servidor — nunca apresentar premissa como dado válido. */
  serverError: unknown;
  onRetry: () => void;
}

// Aviso de origem dos parâmetros de produção no sheet.
// Três estados, decididos pelos campos do preview do servidor (productionCounter):
//  - erro → falha ao carregar o dado medido: estado de erro padrão, com retry;
//  - estrutural → SEM contador de produção: permanente, com CTA de configurar o contador;
//  - transitório→ contador existe mas volume < piso na janela: sem CTA (resolve com a linha rodando).
// Sem dado do servidor ainda (productionCounter ausente) ⇒ não renderiza nada (evita aviso falso).
export function ParamOriginAlert({ machine, serverError, onRetry }: Props) {
  if (serverError) {
    return (
      <QueryErrorState
        variant="inline"
        title="Não foi possível carregar os dados medidos"
        error={serverError}
        onRetry={onRetry}
      />
    );
  }

  const counter = machine.productionCounter;
  if (!counter) return null;

  if (!counter.configured) {
    return (
      <Banner variant="warning" title="Ritmo, ciclo e qualidade estão como premissa">
        Esta máquina não tem um contador de produção. Para medi-los a partir da produção real,
        configure na máquina real um parâmetro contador (&quot;conta unidades&quot;) que influencia
        a produção.
      </Banner>
    );
  }

  if (counter.producedInWindow < MIN_PRODUCED_FOR_MEASURED) {
    return (
      <Banner variant="info" title="Ainda faltam dados na janela de calibração">
        {counter.producedInWindow} de {MIN_PRODUCED_FOR_MEASURED} unidades produzidas na janela —
        ritmo e qualidade seguem como premissa até acumular volume (resolve com a linha rodando).
      </Banner>
    );
  }

  return null;
}
